import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { FormArray, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { from } from 'rxjs';
import { ProposalRequestForm } from 'src/app/classes/form-groups/proposal-request-form';
import { ProposalRequestVendorForm } from 'src/app/classes/form-groups/proposal-request-vendor-form';
import { Company, ProposalRequestVendor } from 'src/app/classes/models';
import { AddProposalRequestNonViveVendorsModalComponent } from 'src/app/modals/add-proposal-request-non-vive-vendors-modal/add-proposal-request-non-vive-vendors-modal.component';
import { AddProposalRequestViveVendorsModalComponent } from 'src/app/modals/add-proposal-request-vive-vendors-modal/add-proposal-request-vive-vendors-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { EditProposalRequestVendorModalComponent } from 'src/app/modals/edit-proposal-request-vendor-modal/edit-proposal-request-vendor-modal.component';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { atLeastOneSelectionValidator } from 'src/app/validators/at-least-one-selection-validator';

@Component({
  selector: 'app-quicksource-add-vendors',
  templateUrl: './quicksource-add-vendors.component.html',
  styleUrls: ['./quicksource-add-vendors.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class QuicksourceAddVendorsComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private ModalService: NgbModal,
    private SharedDataService: SharedDataService,
    private route: ActivatedRoute,
    private ToastService: ToastService
  ) {
    this.SharedDataService.listenForEvent<
      ProposalRequestForm | Company | boolean
    >().subscribe({
      next: ({ eventName, data }) => {
        if (
          eventName === 'SetProposalRequestForm' &&
          data &&
          !(data instanceof Company) &&
          typeof data !== 'boolean'
        ) {
          this.ProposalRequestFormGroup = data;
          this._cdr.markForCheck();
        }

        if (eventName === 'SetCompany' && data instanceof Company) {
          this.Company = data;
          this._cdr.markForCheck();
        }

        if (eventName === 'SetDisableInput' && typeof data === 'boolean') {
          this.disableInput = data;
          this._cdr.markForCheck();
        }
      }
    });

    this.SharedDataService.sendEvent('GetProposalRequestForm');
    this.SharedDataService.sendEvent('GetCompany');
    this.SharedDataService.sendEvent('GetDisableInput');

    this.route.paramMap.subscribe({
      next: (paramMap) => {
        const ProposalRequestId = Number(paramMap.get('ProposalRequestId'));

        if (!ProposalRequestId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Proposal Request Id.',
            { returnError: true }
          );
        }

        this.ProposalRequestId = ProposalRequestId;
        this._cdr.markForCheck();
      }
    });
  }

  ProposalRequestFormGroup!: ProposalRequestForm;
  ViveProposalRequestVendorsFormArray =
    new FormArray<ProposalRequestVendorForm>([]);
  NonViveProposalRequestVendorsFormArray =
    new FormArray<ProposalRequestVendorForm>([]);
  ProposalRequestId?: number;
  Company!: Company;
  disableInput: boolean = false;

  editVendor(ProposalRequestVendorFormGroup: ProposalRequestVendorForm) {
    const { componentInstance, result } = this.ModalService.open(
      EditProposalRequestVendorModalComponent,
      {
        backdrop: 'static',
        modalDialogClass: 'add-proposal-request-vendor-modal'
      }
    );

    const { value: ProposalRequestVendor } = ProposalRequestVendorFormGroup;
    const ProposalRequestVendorFormGroupClone = new ProposalRequestVendorForm();
    ProposalRequestVendorFormGroupClone.patchValue(ProposalRequestVendor);

    Object.assign(componentInstance, {
      ProposalRequestVendorFormGroup: ProposalRequestVendorFormGroupClone,
      Company: this.Company
    });

    from(result).subscribe({
      next: (SavedProposalRequestVendorFormGroup) => {
        const { value: ProposalRequestVendor } =
          SavedProposalRequestVendorFormGroup;
        ProposalRequestVendorFormGroup.patchValue(ProposalRequestVendor);
        this._cdr.markForCheck();
      }
    });
  }

  setProposalRequestVendors() {
    const ProposalRequestVendorsFormArray =
      this.ProposalRequestFormGroup.controls['ProposalRequestVendors'];
    ProposalRequestVendorsFormArray!.clear();
    this.ViveProposalRequestVendorsFormArray.controls.forEach((prvForm) => {
      ProposalRequestVendorsFormArray!.push(prvForm);
    });
    this.NonViveProposalRequestVendorsFormArray.controls.forEach((prvForm) => {
      ProposalRequestVendorsFormArray!.push(prvForm);
    });
    this._cdr.markForCheck();
  }

  addViveVendor() {
    const ProposalRequestVendorsFormArray =
      this.ProposalRequestFormGroup.controls['ProposalRequestVendors'];
    const ProposalRequestViveVendorsModal = this.ModalService.open(
      AddProposalRequestViveVendorsModalComponent,
      {
        backdrop: 'static',
        modalDialogClass: 'add-proposal-request-vendor-modal'
      }
    );
    const { componentInstance } = ProposalRequestViveVendorsModal;

    const ProposalRequestVendorsMap: Map<number, ProposalRequestVendor> =
      new Map();
    ProposalRequestVendorsFormArray!.value.forEach((prv) => {
      const { VendorId } = prv;
      if (VendorId) {
        ProposalRequestVendorsMap.set(VendorId, prv);
      }
    });

    Object.assign(componentInstance, {
      ProposalRequestVendorsMap: ProposalRequestVendorsMap,
      ProposalRequest: this.ProposalRequestFormGroup.value
    });

    const { result } = ProposalRequestViveVendorsModal;
    from(result).subscribe({
      next: (ProposalRequestVendorsMap) => {
        this.ViveProposalRequestVendorsFormArray.clear();
        ProposalRequestVendorsMap.forEach((prv: ProposalRequestVendor) => {
          const ProposalRequestVendorFormGroup =
            new ProposalRequestVendorForm();
          ProposalRequestVendorFormGroup.patchValue(prv);
          this.ViveProposalRequestVendorsFormArray.push(
            ProposalRequestVendorFormGroup
          );
        });
        this.setProposalRequestVendors();
        this._cdr.markForCheck();
      }
    });
  }

  addNonViveVendor() {
    const ProposalRequestNonViveVendorsModal = this.ModalService.open(
      AddProposalRequestNonViveVendorsModalComponent,
      {
        backdrop: 'static',
        modalDialogClass: 'add-proposal-request-vendor-modal'
      }
    );
    const { componentInstance, result } = ProposalRequestNonViveVendorsModal;

    Object.assign(componentInstance, {
      Company: this.Company,
      ProposalRequest: this.ProposalRequestFormGroup.value
    });

    from(result).subscribe({
      next: (
        SavedProposalRequestVendorFormArray: FormArray<ProposalRequestVendorForm>
      ) => {
        SavedProposalRequestVendorFormArray.controls.forEach(
          (ProposalRequestVendorForm) => {
            this.NonViveProposalRequestVendorsFormArray.push(
              ProposalRequestVendorForm
            );
          }
        );
        this.setProposalRequestVendors();
        this._cdr.markForCheck();
      }
    });
  }

  removeVendor(
    ProposalRequestVendorFormArray: FormArray<ProposalRequestVendorForm>,
    index: number
  ) {
    const { componentInstance, result } = this.ModalService.open(
      ConfirmModalComponent,
      { backdrop: 'static', size: 'sm' }
    );
    Object.assign(componentInstance, {
      displayText: 'Are you sure you wish to delete the vendor?'
    });

    from(result).subscribe({
      next: (confirm) => {
        if (confirm) {
          ProposalRequestVendorFormArray.removeAt(index);
          this.setProposalRequestVendors();
          this._cdr.markForCheck();
        }
      }
    });
  }

  ngOnInit(): void {
    if (!this.ProposalRequestFormGroup) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error loading the proposal form data.',
        { returnError: true }
      );
    }

    this.ProposalRequestFormGroup.addControl(
      'ProposalRequestVendors',
      new FormArray<ProposalRequestVendorForm>([])
    );
    this.ProposalRequestFormGroup.controls[
      'ProposalRequestVendors'
    ]!.setValidators([atLeastOneSelectionValidator]);
    this.ProposalRequestFormGroup.controls[
      'ProposalRequestVendors'
    ]!.updateValueAndValidity();

    this.ProposalRequestFormGroup.controls[
      'ProposalRequestVendors'
    ]!.controls.forEach((ProposalRequestVendorForm) => {
      const { VendorId } = ProposalRequestVendorForm.value;

      if (VendorId) {
        this.ViveProposalRequestVendorsFormArray.push(
          ProposalRequestVendorForm
        );
      } else {
        this.NonViveProposalRequestVendorsFormArray.push(
          ProposalRequestVendorForm
        );
      }
    });
  }
}
